import { Sun, Moon, LogOut, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/Button'
import { FontSizeToggle } from './FontSizeToggle'
import { auth as authApi } from '@/api/client'

interface HeaderProps {
  title: string
  description?: string
}

export function Header({ title, description }: HeaderProps) {
  const { resolvedTheme, setTheme, setMobileSidebarOpen } = useUIStore()
  const { user, logout } = useAuthStore()

  function handleThemeToggle() {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  async function handleLogout() {
    // Tell the server to blacklist the refresh jti and emit a cookie-clear
    // Set-Cookie response. Don't block the UI if the call fails (offline,
    // rate-limited, etc.) — the local state-wipe still kicks us to /login.
    try {
      await authApi.logout()
    } catch {
      // Intentional: local logout is the source of truth for the user,
      // server-side revocation is best-effort.
    }
    logout()
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex items-center justify-between border-b border-border px-6 py-4 mb-6',
        'bg-background/80 backdrop-blur-sm'
      )}
    >
      {/* Left: hamburger + page title */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile hamburger */}
        <Button
          variant="ghost"
          size="icon-sm"
          className="lg:hidden shrink-0"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open sidebar"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight truncate">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground truncate">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Right: controls */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Font size */}
        <FontSizeToggle />

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleThemeToggle}
          aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {resolvedTheme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>

        {/* User info */}
        {user && (
          <div className="hidden sm:flex flex-col items-end text-right">
            <span className="text-sm font-medium leading-none">
              {user.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {user.email}
            </span>
          </div>
        )}

        {/* Logout */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleLogout}
          aria-label="Logout"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  )
}
